import Button from "../core/Button";
import ResizableScene from "../core/ResizableScene";
import Scrollbar from "./Scrollbar";

type Point = {x: number; y: number};

export default class ScrollRect {

    scene: ResizableScene;

    content: Phaser.GameObjects.Container;
    viewport: Phaser.GameObjects.Container;
    scrollbar: Scrollbar;

    events: Phaser.Events.EventEmitter;

    pointerDown = false;

    minY: number;
    maxY: number;
    vy = 0;
    targetY: number;

    dragStartPoint: Point;
    dragPoints: Point[] = [];
    dragDistance = 0;

    fixedDeltaTime = 0.016;
    accum = 0;

    constructor(scene: ResizableScene, content: Phaser.GameObjects.Container, viewport: Phaser.GameObjects.Container, scrollbar?: Scrollbar) {
        this.scene = scene;
        this.content = content;
        this.viewport = viewport;
        if (scrollbar) this.scrollbar = scrollbar;

        this.events = new Phaser.Events.EventEmitter();

        this.calculateBounds();

        this.viewport.setInteractive();
        this.viewport.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.onPointerDown, this);

        this.scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove, this);
        this.scene.input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp, this);
    }

    enableScrolling(button: Button): void {
        const buttonDown = button.buttonDown;
        button.buttonDown = (ptr: Phaser.Input.Pointer): void => {
            this.onPointerDown(ptr);
            buttonDown.call(button, ptr);
        };

        const buttonUp = button.buttonUp;
        button.buttonUp = (): void => {
            if (this.dragDistance > 50) return;
            buttonUp.call(button);
        };
    }

    enable(): void {
        this.viewport.setInteractive();
        this.scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove, this);
        this.scene.input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp, this);
    }

    disable(): void {
        this.viewport.disableInteractive();
        this.pointerDown = false;
        this.vy = 0;
        this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove, this);
        this.scene.input.off(Phaser.Input.Events.POINTER_UP, this.onPointerUp, this);
    }

    calculateBounds(): void {
        const viewportMin = this.viewport.y - this.viewport.height / 2;
        const viewportMax = this.viewport.y + this.viewport.height / 2;
        this.maxY = viewportMin + this.content.height / 2;
        this.minY = viewportMax - this.content.height / 2;
        this.targetY = this.content.y;
    }

    calculateProgress(y: number): number {
        let calProgress = - ((y - this.minY)/(this.maxY - this.minY) - 1);
        calProgress = Phaser.Math.Clamp(calProgress, 0, 1);
        return calProgress;
    }

    setProgress(progress: number): void {
        progress = Phaser.Math.Clamp(progress, 0, 1);
        this.targetY = this.minY + (this.maxY - this.minY) * (1 - progress);
        if (this.scrollbar) this.scrollbar.setProgress(progress);
    }

    getProgress(): number {
        return - ((this.targetY - this.minY)/(this.maxY - this.minY) - 1);
    }

    update(dt: number): void {
        // fixed delta time prevents server/client scroll rects from getting out of sync
        this.accum += dt;
        while (this.accum > this.fixedDeltaTime) {
            const dt = this.fixedDeltaTime;
            this.accum -= this.fixedDeltaTime;

            this.content.y -= (this.content.y - this.targetY) * 10 * dt;
            this.targetY += this.vy * dt;
            const delta = this.vy * 8 * dt;
            this.vy -= delta >= 0 ? Math.min(delta, this.vy) : Math.max(delta, this.vy);
        }
        
        this.constrainToBounds();
        if (this.scrollbar) this.scrollbar.setProgress(this.getProgress());

        if (Math.abs(this.vy) <= 0.15 && this.vy != 0) {
            this.vy = 0;
        }
    }

    onPointerDown(ptr: Phaser.Input.Pointer): void {
        const pos = new Phaser.Math.Vector2(ptr.worldX, ptr.worldY);
        this.pointerDown = true;
        this.dragStartPoint = pos;
        this.vy = 0;
        this.dragDistance = 0;
    }

    onPointerMove(ptr: Phaser.Input.Pointer): void {
        const pos = new Phaser.Math.Vector2(ptr.worldX, ptr.worldY);
        if (this.pointerDown) {
            if (this.dragStartPoint === undefined) this.dragStartPoint = {x: pos.x, y: pos.y};
            const dragUpdatePoint = {x: pos.x, y: pos.y};
            const delta = {x: dragUpdatePoint.x - this.dragStartPoint.x, y: dragUpdatePoint.y - this.dragStartPoint.y};

            this.content.y = this.content.y + delta.y;
            this.targetY = this.content.y;
            this.constrainToBounds();

            if (this.scrollbar) this.scrollbar.setProgress(this.getProgress());

            this.dragDistance += Math.abs(delta.y);
            this.dragPoints.unshift(delta);
            this.dragPoints.unshift(delta);
            if (this.dragPoints.length > 8) this.dragPoints.pop();

            this.dragStartPoint.x = pos.x;
            this.dragStartPoint.y = pos.y;
        }
    }

    constrainToBounds(): void {
        if (this.content.y < this.minY) {
            this.content.y = this.minY;
            this.targetY = this.content.y;
        }
        if (this.content.y > this.maxY) {
            this.content.y = this.maxY;
            this.targetY = this.content.y;
        }
    }

    onPointerUp(): void {
        if (this.pointerDown) {
            this.pointerDown = false;

            this.dragDistance = 0;
            this.dragStartPoint = undefined;
            if (this.dragPoints.length > 0) {
                // let avgX = 0;
                let avgY = 0;
                for (const delta of this.dragPoints) {
                    // avgX += delta.x;
                    avgY += delta.y;
                }
                // avgX /= this.dragPoints.length;
                avgY /= this.dragPoints.length;
                const vy = (avgY / 0.017);
                this.vy += vy;
            }
            this.dragPoints = [];
        }
    }

    setScrollbarProgress(progress: number): void {
        this.setProgress(progress);
    }

    setVelocity(data: { vel: number; progress: number}): void {
        this.vy = data.vel;
        this.setProgress(data.progress);
    }

    isPointerDown(): boolean {
        return this.pointerDown;
    }
}
